import React from 'react';
import { MeetingTranscriptionItem } from '../services/api';

interface TranscriptListProps {
  transcripts: MeetingTranscriptionItem[];
  selectedTranscriptId: string | null;
  onSelectTranscript: (transcriptId: string) => void;
  onDeleteTranscript?: (transcriptId: string) => void;
}

const TranscriptList: React.FC<TranscriptListProps> = ({ 
  transcripts, 
  selectedTranscriptId, 
  onSelectTranscript,
  onDeleteTranscript 
}) => {
  const getTranscriptStatusBadge = (transcript: MeetingTranscriptionItem): string | null => {
    if (transcript.status === 'capturing') return 'Capturing...';
    if (transcript.status === 'processing' || transcript.status === 'uploading_done') {
      return 'Processing...';
    }
    if (transcript.status === 'completed') return 'Completed';
    if (transcript.status === 'failed' || transcript.status === 'cancelled') {
      return transcript.status;
    }
    return transcript.status || null;
  };

  return (
    <div className="w-full h-full flex flex-col bg-gray-50">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-white">
        <h2 className="text-xl font-bold text-gray-800">Meeting Transcripts</h2>
        <p className="text-xs text-gray-500 mt-1">
          Select a transcript to view details
        </p>
      </div>
      <div className="flex-1 overflow-y-auto">
        {transcripts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No transcripts yet</div>
        ) : (
          transcripts.map((transcript) => (
            <div
              key={transcript.id}
              className={`group relative flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-200 bg-white transition-colors ${
                selectedTranscriptId === transcript.id
                  ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-l-4 border-indigo-500'
                  : 'hover:bg-gray-50'
              } ${['capturing', 'processing', 'uploading_done'].includes(transcript.status) ? 'border-l-4 border-blue-500' : ''}`}
            >
              <div
                className={`flex-1 min-w-0 ${
                  transcript.status === 'completed' ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                }`}
                onClick={() => {
                  if (transcript.status === 'completed') {
                    onSelectTranscript(transcript.id);
                  }
                }}
              >
                <div className="text-sm font-semibold text-gray-800 mb-1 truncate">
                  {transcript.title || 'Untitled meeting'}
                </div>
                <div className="flex items-center justify-between gap-2">
                  {getTranscriptStatusBadge(transcript) && (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      transcript.status === 'completed'
                        ? 'text-green-600 bg-green-100'
                        : ['capturing', 'processing', 'uploading_done'].includes(transcript.status)
                          ? 'text-blue-600 bg-blue-100'
                          : transcript.status === 'failed'
                            ? 'text-red-600 bg-red-100'
                            : 'text-gray-600 bg-gray-100'
                    }`}>
                      {getTranscriptStatusBadge(transcript)}
                    </span>
                  )}
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {new Date(transcript.createdAt).toLocaleString()}
                  </span>
                </div>
              </div>
              {onDeleteTranscript && (transcript.status === 'completed' || transcript.status === 'failed' || transcript.status === 'cancelled') && (
                <button
                  className="ml-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-all opacity-0 group-hover:opacity-100 flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm('Are you sure you want to delete this transcript?')) {
                      onDeleteTranscript(transcript.id);
                    }
                  }}
                  title="Delete transcript"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18"></path>
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                  </svg>
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TranscriptList;
